import { useState, useEffect } from 'react'

import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import TableBody from '@mui/material/TableBody'
import AutocompleteSelect from '../../../common/components/AutocompleteSelect'
import Alert from '@mui/material/Alert'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'

interface Props {
    settings: Record<string, unknown>
    shops: Array<{ id: number; shop_name: string; channel?: { name: string } }>
    accounts: Array<{ id: string | number; name: string; type: string }>
    setSettings: React.Dispatch<React.SetStateAction<Record<string, unknown>>>
}

export default function WalmartGLAccounts({ settings, shops, accounts, setSettings }: Props) {
    type Row = {
        shop_id: number | ''
        fee_category: string
        fee_type: string
        fee_subtype: string
        account_id: string
    }

    const accountOptions: [string, string][] = accounts.map((a) => [a.name, String(a.id)])

    const feeMapArr = (settings.walmart_fee_map as unknown[]) || []
    const initialRows: Row[] = Array.isArray(feeMapArr)
        ? feeMapArr.map((entry) => {
              if (Array.isArray(entry) && entry.length === 5) {
                  return {
                      shop_id: typeof entry[0] === 'number' ? entry[0] : '',
                      fee_category: String(entry[1] ?? ''),
                      fee_type: String(entry[2] ?? ''),
                      fee_subtype: String(entry[3] ?? ''),
                      account_id: String(entry[4] ?? ''),
                  }
              }
              return { shop_id: '', fee_category: '', fee_type: '', fee_subtype: '', account_id: '' }
          })
        : []

    const [rows, setRows] = useState<Row[]>(() => {
        if (initialRows.length) return initialRows
        return shops.length > 0
            ? shops.map((s) => ({ shop_id: s.id, fee_category: '', fee_type: '', fee_subtype: '', account_id: '' }))
            : [{ shop_id: '', fee_category: '', fee_type: '', fee_subtype: '', account_id: '' }]
    })

    useEffect(() => {
        const feeMapArr = (settings.walmart_fee_map as unknown[]) || []
        const newRows: Row[] = Array.isArray(feeMapArr) && feeMapArr.length
            ? feeMapArr.map((entry) => {
                  if (Array.isArray(entry) && entry.length === 5) {
                      return {
                          shop_id: typeof entry[0] === 'number' ? entry[0] : '',
                          fee_category: String(entry[1] ?? ''),
                          fee_type: String(entry[2] ?? ''),
                          fee_subtype: String(entry[3] ?? ''),
                          account_id: String(entry[4] ?? ''),
                      }
                  }
                  return { shop_id: '', fee_category: '', fee_type: '', fee_subtype: '', account_id: '' }
              })
            : shops.length > 0
            ? shops.map((s) => ({ shop_id: s.id, fee_category: '', fee_type: '', fee_subtype: '', account_id: '' }))
            : [{ shop_id: '', fee_category: '', fee_type: '', fee_subtype: '', account_id: '' }]
        setRows(newRows)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settings.walmart_fee_map, shops])

    const handleChange = <K extends keyof Row>(idx: number, field: K, value: Row[K]) => {
        setRows((prev) => {
            const updated = prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r))
            setSettings((s: Record<string, unknown>) => ({
                ...s,
                walmart_fee_map: updated.map((r) => [r.shop_id, r.fee_category, r.fee_type, r.fee_subtype, r.account_id]),
            }))
            return updated
        })
    }

    const handleAdd = () => {
        setRows((prev) => {
            const updated = [...prev, { shop_id: '', fee_category: '', fee_type: '', fee_subtype: '', account_id: '' }] as Row[]
            setSettings((s: Record<string, unknown>) => ({
                ...s,
                walmart_fee_map: updated.map((r) => [r.shop_id, r.fee_category, r.fee_type, r.fee_subtype, r.account_id]),
            }))
            return updated
        })
    }

    const handleDelete = (idx: number) => {
        setRows((prev) => {
            const updated = prev.filter((_, i) => i !== idx)
            setSettings((s: Record<string, unknown>) => ({
                ...s,
                walmart_fee_map: updated.map((r) => [r.shop_id, r.fee_category, r.fee_type, r.fee_subtype, r.account_id]),
            }))
            return updated
        })
    }

    const renderSelect = (
        idx: number,
        field: keyof Omit<Row, 'fee_type' | 'fee_category' | 'fee_subtype'>,
        options: [string, string | number][],
    ) => {
        const optionObjs = options.map(([label, value]) => ({ label, value }))
        return (
            <AutocompleteSelect
                options={optionObjs}
                value={rows[idx][field]}
                onChange={(val) => handleChange(idx, field, val as Row[typeof field])}
                fullWidth
            />
        );
    };

    return (
        <Paper elevation={0} sx={{ p: 2 }}>
            {/* <Stack direction="row" justifyContent="flex-end">
                <Button
                    variant="outlined"
                    color="secondary"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={handleAdd}
                >
                    Add
                </Button>
            </Stack> */}
            {rows.length === 0 ? (
                <Alert severity="info" variant="outlined" sx={{ borderColor: 'grey.300', marginTop: '1rem' }}>
                    No fees defined yet
                </Alert>
            ) : (
                <Table size="small" sx={{ mt: 1 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', py: 0.75, px: 1, fontSize: '0.8125rem', width: 200 }}>
                                Connection
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold', py: 0.75, px: 1, fontSize: '0.8125rem', width: 200 }}>
                                Fee Category
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold', py: 0.75, px: 1, fontSize: '0.8125rem', width: 200 }}>
                                Fee Type
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold', py: 0.75, px: 1, fontSize: '0.8125rem', width: 200 }}>
                                Fee SubType
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold', py: 0.75, px: 1, fontSize: '0.8125rem', width: 200 }}>
                                Account
                            </TableCell>
                            <TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((row, idx) => (
                            <TableRow key={idx} sx={{ bgcolor: idx % 2 ? 'grey.50' : 'common.white' }}>
                                <TableCell sx={{ px: 1, py: 0.75 }}>
                                    {(() => {
                                        const shop = shops.find((s) => s.id === row.shop_id)
                                        if (!shop) return ''
                                        const channelName = shop.channel?.name ?? ''
                                        return channelName ? `${shop.shop_name} (${channelName})` : shop.shop_name
                                    })()}
                                </TableCell>
                                <TableCell sx={{ px: 1, py: 0.75 }}>
                                    <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>{row.fee_category}</Typography>
                                </TableCell>
                                <TableCell sx={{ px: 1, py: 0.75 }}>
                                    <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>{row.fee_type}</Typography>
                                </TableCell>
                                <TableCell sx={{ px: 1, py: 0.75 }}>
                                    <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>{row.fee_subtype}</Typography>
                                </TableCell>
                                <TableCell sx={{ px: 1, py: 0.75 }}>{renderSelect(idx, 'account_id', accountOptions)}</TableCell>
                                <TableCell sx={{ width: 96, py: 0.75, px: 1 }}>
                                    <IconButton
                                        color="inherit"
                                        sx={{ color: 'grey.500' }}
                                        onClick={() => handleDelete(idx)}
                                        size="small"
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}
        </Paper>
    )
}
